import React, { useState, useEffect, useRef } from 'react';
import { X, Clock, User, Calendar, MapPin, Phone, Mail, FileText, AlertCircle, Navigation, CheckCircle, Smartphone, Wifi, ExternalLink, Map, AlertTriangle, Edit, Trash2 } from 'lucide-react';
import { collection, getDocs, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { getCheckInRadius, getCurrentPosition, geocodeAddress, calculateDistance, validateCheckIn } from '../utils/geolocation';
import { formatAddress, getNavigationLinks, isValidAddress } from '../utils/addressFormatter';
import CheckoutNotesModal from './CheckoutNotesModal';
import { sha256 } from 'js-sha256';

// Configuration des créneaux horaires
const TIME_SLOTS = [
  { id: 'morning', label: 'Matin', timeRange: '04:00 - 12:00' },
  { id: 'noon', label: 'Midi', timeRange: '12:00 - 14:00' },
  { id: 'afternoon', label: 'Après-midi', timeRange: '14:00 - 17:30' },
  { id: 'evening', label: 'Soir', timeRange: '17:30 - 22:00' },
];

const EnhancedAppointmentDetailsModal = ({
  appointment,
  onClose,
  onUpdate,
  onEdit,
  onDelete,
  canEdit,
  size = "lg",
  db
}) => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  const [distanceCalculating, setDistanceCalculating] = useState(false);
  const [distance, setDistance] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [clientContract, setClientContract] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(appointment.completedTasks || []);
  const [isNfcAvailable, setIsNfcAvailable] = useState(false);
  const [isWithinRange, setIsWithinRange] = useState(false);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(appointment.checkInStatus || null);
  const [showCheckoutNotes, setShowCheckoutNotes] = useState(false);
  const [isNfcReading, setIsNfcReading] = useState(false);
  const [nfcError, setNfcError] = useState(null);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [taskPriorities, setTaskPriorities] = useState({});
  const abortControllerRef = useRef(null);

  if (!appointment) return null;

  // Fonction pour formater la date
  const formatDate = (date) => {
    if (!date) return 'Date non spécifiée';
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Fonction pour déterminer le créneau horaire
  const getTimeSlot = (startTime) => {
    if (!startTime) return '';
    const hour = parseInt(startTime.split(':')[0]);
    if (hour < 12) return 'Matin';
    if (hour < 18) return 'Après-midi';
    return 'Soir';
  };

  // Fonction pour obtenir le statut avec badge coloré
  const getStatusBadge = (status) => {
    const statusConfig = {
      libre: {
        text: 'Plage à combler',
        class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      },
      reserve: {
        text: 'En attente de validation',
        class: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      },
      assigne: {
        text: 'Assigné',
        class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      },
      in_progress: {
        text: 'En cours',
        class: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      },
      completed: {
        text: 'Terminé',
        class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      }
    };

    const config = statusConfig[status] || {
      text: status || 'Non défini',
      class: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.class}`}>
        {config.text}
      </span>
    );
  };

  // Fonction pour ouvrir la navigation avec gestion des fallbacks iOS
  const openNavigation = (app, navigationType = 'waze') => {
    if (!app.clientAddress) return;

    // Formater l'adresse si c'est un objet
    const formattedAddress = formatAddress(app.clientAddress);
    const navigationLinks = getNavigationLinks(formattedAddress);
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

    // Sélectionner le bon lien selon le type de navigation
    let primaryLink, fallbackLink;

    switch (navigationType) {
      case 'waze':
        primaryLink = navigationLinks.waze;
        fallbackLink = navigationLinks.wazeFallback;
        break;
      case 'google':
        primaryLink = navigationLinks.googleMaps;
        fallbackLink = navigationLinks.googleMapsFallback;
        break;
      case 'apple':
        primaryLink = navigationLinks.appleMaps;
        fallbackLink = navigationLinks.appleMaps; // Apple Maps n'a pas besoin de fallback
        break;
      default:
        primaryLink = navigationLinks.waze;
        fallbackLink = navigationLinks.wazeFallback;
    }

    // Ouvrir avec gestion des fallbacks
    if (isIOS) {
      // Sur iOS, essayer d'abord le schéma natif, puis le fallback si nécessaire
      const navLink = document.createElement('a');
      navLink.href = primaryLink;
      navLink.target = '_blank';

      // Pour Apple Maps, ouvrir directement (fonctionne toujours)
      if (navigationType === 'apple') {
        navLink.click();
      } else {
        // Pour Waze et Google Maps, vérifier si l'application est installée
        setTimeout(() => {
          // Si nous sommes toujours sur la page après 500ms, l'application n'est pas installée
          if (document.hasFocus()) {
            window.open(fallbackLink, '_blank');
          }
        }, 500);

        navLink.click();
      }
    } else {
      // Sur Android/Desktop, ouvrir directement
      window.open(primaryLink, '_blank');
    }
  };

  // Fonction pour pointer avec GPS avec gestion de compatibilité iOS/Android
  const pointWithGPS = async () => {
    const platform = detectPlatform();

    if (!appointment.clientAddress) {
      alert("Adresse du client non disponible");
      return;
    }

    try {
      // Vérifier si la géolocalisation est disponible
      if (!navigator.geolocation) {
        const errorMessage = platform.isIOS
          ? "La géolocalisation n'est pas disponible sur votre appareil iOS. Veuillez utiliser Safari pour le pointage GPS."
          : "La géolocalisation n'est pas disponible sur votre navigateur.";
        alert(errorMessage);
        return;
      }

      // Vérifier les permissions sur iOS/Chrome
      if (platform.isIOS && platform.isChrome) {
        alert("⚠️ Sur iOS, Chrome peut avoir des problèmes avec la géolocalisation. Nous recommandons d'utiliser Safari pour le pointage GPS.");
      }

      // Récupérer le rayon de pointage configuré
      const checkInRadius = await getCheckInRadius();
      console.log('Rayon de pointage configuré:', checkInRadius);

      setDistanceCalculating(true);

      try {
        // Utiliser la vraie géolocalisation
        const currentPosition = await getCurrentPosition();
        const clientCoords = await geocodeAddress(appointment.clientAddress);

        const validation = validateCheckIn(
          { lat: currentPosition.lat, lng: currentPosition.lng },
          clientCoords,
          checkInRadius
        );

        setDistanceCalculating(false);

        if (validation.valid) {
          // Pointage GPS réussi
          handleCheckIn('gps');
          setDistance(validation.formattedDistance);
          setIsWithinRange(true);
        } else {
          // Hors de la zone
          alert(`❌ ${validation.error}\n\nDistance mesurée : ${validation.formattedDistance}\nDistance maximale autorisée : ${checkInRadius} mètres`);
          setDistance(validation.formattedDistance);
          setIsWithinRange(false);
        }

      } catch (gpsError) {
        setDistanceCalculating(false);

        // Messages d'erreur spécifiques par plateforme
        let errorMessage = "Erreur lors de la vérification GPS. ";

        if (platform.isIOS) {
          if (platform.isChrome) {
            errorMessage += "Sur iOS, Chrome peut avoir des problèmes avec la géolocalisation. Essayez avec Safari ou vérifiez les permissions de localisation dans les paramètres.";
          } else {
            errorMessage += "Vérifiez que la géolocalisation est activée dans Réglages > Confidentialité > Service de localisation > Safari.";
          }
        } else if (platform.isAndroid) {
          errorMessage += "Vérifiez que la géolocalisation est activée et que vous avez accordé les permissions à l'application.";
        } else {
          errorMessage += "Vérifiez que la géolocalisation est activée dans votre navigateur.";
        }

        alert(errorMessage);
        console.error("Erreur GPS:", gpsError);
      }

    } catch (error) {
      console.error("Erreur lors du pointage GPS:", error);
      setDistanceCalculating(false);
      alert("Erreur lors de la vérification de la distance. Veuillez réessayer.");
    }
  };

  // Fonction pour déterminer le créneau horaire technique
  const getTechnicalTimeSlot = (startTime) => {
    if (!startTime) return 'morning';
    const hour = parseInt(startTime.split(':')[0]);
    if (hour >= 4 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 14) return 'noon';
    if (hour >= 14 && hour < 17.5) return 'afternoon';
    if (hour >= 17.5 && hour < 22) return 'evening';
    return 'morning';
  };

  // Fonction pour récupérer les tâches
  const fetchTasks = async () => {
    if (!db) return;
    try {
      const tasksCollection = collection(db, 'tasks');
      const taskSnapshot = await getDocs(tasksCollection);
      const taskList = taskSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log("Tâches récupérées:", taskList);
      setTasks(taskList);
    } catch (error) {
      console.error("Erreur lors de la récupération des tâches:", error);
    }
  };

  // Fonction pour récupérer le contrat client
  const fetchClientContract = async () => {
    if (!db || !appointment.clientId) return;
    try {
      console.log("Recherche du contrat pour clientId:", appointment.clientId);

      // Chercher le contrat du client directement par ID (comme dans AppointmentDetailsModal)
      const contractDoc = await getDoc(doc(db, 'contracts', appointment.clientId));

      if (contractDoc.exists()) {
        const contract = { id: contractDoc.id, ...contractDoc.data() };
        console.log("Contrat trouvé:", contract);
        console.log("tasksPerPeriod du contrat:", contract.tasksPerPeriod);
        setClientContract(contract);
      } else {
        console.log("Aucun contrat trouvé pour ce client avec ID:", appointment.clientId);

        // Fallback: chercher dans toute la collection si le contrat n'existe pas par ID direct
        const contractsRef = collection(db, 'contracts');
        const contractsSnapshot = await getDocs(contractsRef);
        const clientContracts = contractsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(contract => contract.clientId === appointment.clientId);

        console.log("Contrats trouvés par clientId:", clientContracts);

        if (clientContracts.length > 0) {
          const contract = clientContracts[0];
          console.log("Contrat sélectionné (fallback):", contract);
          setClientContract(contract);
        } else {
          console.log("Aucun contrat trouvé pour ce client");
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du contrat client:", error);
    }
  };

  // Fonction pour récupérer l'historique des rendez-vous du bénéficiaire
  const fetchAppointmentHistory = async () => {
    if (!db || !appointment.clientId) return;

    setLoadingHistory(true);
    try {
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);

      const allAppointments = appointmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));

      // Filtrer les rendez-vous du même client, terminés, avec des notes
      const clientAppointments = allAppointments
        .filter(app =>
          app.clientId === appointment.clientId &&
          app.status === 'completed' &&
          (app.notes?.content || (app.checkInStatus && app.checkInStatus.notes)) &&
          app.id !== appointment.id // Exclure le rendez-vous actuel
        )
        .sort((a, b) => b.date - a.date) // Tri décroissant par date
        .slice(0, 5); // Limiter aux 5 dernières notes

      // Enrichir avec les noms des employés si employeeName n'est pas déjà présent
      const enrichedAppointments = await Promise.all(
        clientAppointments.map(async (app) => {
          // Si employeeName existe déjà, ne pas le remplacer
          if (app.employeeName) {
            return app;
          }

          // Sinon, essayer de récupérer le nom depuis l'employeeId
          if (app.employeeId) {
            try {
              const employeeDoc = await getDoc(doc(db, 'employees', app.employeeId));
              if (employeeDoc.exists()) {
                const employeeData = employeeDoc.data();
                // Construire le nom complet depuis firstName et lastName
                const fullName = `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim();
                return {
                  ...app,
                  employeeName: fullName || 'Intervenant inconnu'
                };
              }
            } catch (error) {
              console.error("Erreur lors de la récupération de l'employé:", error);
            }
          }

          return app;
        })
      );

      console.log("Historique des rendez-vous récupéré:", enrichedAppointments);
      setAppointmentHistory(enrichedAppointments);

      // Calculer les priorités des tâches après avoir récupéré l'historique
      calculateTaskPriorities(enrichedAppointments);

    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique des rendez-vous:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fonction pour calculer les priorités des tâches basées sur la fréquence d'exécution
  const calculateTaskPriorities = (appointments) => {
    if (!appointments || appointments.length === 0) {
      console.log("Aucun historique disponible pour calculer les priorités");
      return;
    }

    const priorities = {};
    const today = new Date();

    // Pour chaque tâche, trouver la dernière date d'exécution
    tasks.forEach(task => {
      let lastExecutionDate = null;

      // Parcourir tous les rendez-vous pour trouver la dernière exécution de cette tâche
      appointments.forEach(app => {
        if (app.completedTasks && app.completedTasks.includes(task.id)) {
          const appointmentDate = app.date instanceof Date ? app.date : new Date(app.date);
          if (!lastExecutionDate || appointmentDate > lastExecutionDate) {
            lastExecutionDate = appointmentDate;
          }
        }
      });

      // Calculer le nombre de jours depuis la dernière exécution
      if (lastExecutionDate) {
        const daysSinceLastExecution = Math.floor((today - lastExecutionDate) / (1000 * 60 * 60 * 24));

        // Déterminer la priorité selon les critères
        if (daysSinceLastExecution <= 2) {
          priorities[task.id] = { priority: 'green', days: daysSinceLastExecution };
        } else if (daysSinceLastExecution <= 4) {
          priorities[task.id] = { priority: 'orange', days: daysSinceLastExecution };
        } else {
          priorities[task.id] = { priority: 'red', days: daysSinceLastExecution };
        }

        console.log(`Tâche "${task.name || task.title}" - Dernière exécution: ${daysSinceLastExecution} jours - Priorité: ${priorities[task.id].priority}`);
      } else {
        // Tâche jamais effectuée - priorité rouge
        priorities[task.id] = { priority: 'red', days: null };
        console.log(`Tâche "${task.name || task.title}" - Jamais effectuée - Priorité: rouge`);
      }
    });

    setTaskPriorities(priorities);
    console.log("Priorités des tâches calculées:", priorities);
  };

  // Fonction pour obtenir la classe CSS selon la priorité
  const getPriorityClass = (taskId) => {
    const priority = taskPriorities[taskId]?.priority;

    switch (priority) {
      case 'green':
        return 'border-l-4 border-l-green-500 bg-green-50 dark:bg-green-900/20';
      case 'orange':
        return 'border-l-4 border-l-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'red':
        return 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20';
      default:
        return 'border-l-4 border-l-gray-300 bg-gray-50 dark:bg-gray-700';
    }
  };

  // Fonction pour obtenir le texte de priorité
  const getPriorityText = (taskId) => {
    const priority = taskPriorities[taskId];
    if (!priority) return '';

    if (priority.days === null) {
      return 'Jamais effectuée';
    }

    switch (priority.priority) {
      case 'green':
        return `Effectuée il y a ${priority.days} jour${priority.days > 1 ? 's' : ''}`;
      case 'orange':
        return `Effectuée il y a ${priority.days} jour${priority.days > 1 ? 's' : ''}`;
      case 'red':
        return `Effectuée il y a ${priority.days} jour${priority.days > 1 ? 's' : ''}`;
      default:
        return '';
    }
  };

  // Fonction pour gérer les checkbox des tâches avec sauvegarde automatique
  const handleTaskToggle = async (taskId) => {
    if (!db) {
      console.error("Base de données non disponible");
      return;
    }

    try {
      // Calculer le nouveau tableau de tâches complétées
      const newCompletedTasks = completedTasks.includes(taskId)
        ? completedTasks.filter(id => id !== taskId)
        : [...completedTasks, taskId];

      // Mettre à jour l'état local immédiatement pour un feedback visuel rapide
      setCompletedTasks(newCompletedTasks);

      // Sauvegarder dans Firestore
      const appointmentRef = doc(db, 'appointments', appointment.id);
      await updateDoc(appointmentRef, {
        completedTasks: newCompletedTasks,
        updatedAt: Timestamp.now()
      });

      console.log("Tâches sauvegardées avec succès:", newCompletedTasks);

    } catch (error) {
      console.error("Erreur lors de la sauvegarde des tâches:", error);

      // Revenir à l'état précédent en cas d'erreur
      setCompletedTasks(completedTasks);

      // Afficher une alerte à l'utilisateur
      alert("Erreur lors de la sauvegarde des tâches. Veuillez réessayer.");
    }
  };

  // Fonction pour vérifier les données NFC
  const verifyNfcData = (nfcData) => {
    try {
      const data = JSON.parse(nfcData);
      const currentDate = new Date();
      const expDate = new Date(data.exp);

      if (currentDate > expDate) {
        return { isValid: false, error: "Le tag NFC a expiré." };
      }

      // Comparer les adresses en les normalisant
      const decodedAddress = atob(data.addr);

      // Formater l'adresse du rendez-vous de la même manière que dans le tag
      let appointmentAddress;
      if (typeof appointment.clientAddress === 'object' && appointment.clientAddress !== null) {
        // Nouvelle structure avec plusieurs champs
        appointmentAddress = formatAddress(appointment.clientAddress);
      } else {
        // Ancienne structure (chaîne simple)
        appointmentAddress = appointment.clientAddress || '';
      }

      // Normaliser les deux adresses pour la comparaison (ignorer espaces et casse)
      const normalizedTagAddress = decodedAddress.toLowerCase().replace(/\s+/g, ' ').trim();
      const normalizedAppointmentAddress = appointmentAddress.toLowerCase().replace(/\s+/g, ' ').trim();

      if (normalizedTagAddress !== normalizedAppointmentAddress) {
        console.warn("Comparaison d'adresses:", {
          tagAddress: decodedAddress,
          appointmentAddress: appointmentAddress,
          normalized: {
            tag: normalizedTagAddress,
            appointment: normalizedAppointmentAddress
          }
        });
        return { isValid: false, error: "L'adresse du tag ne correspond pas à celle du rendez-vous." };
      }

      const secretKey = "Son_of_the_Bitch!"; // À remplacer par une vraie clé secrète
      const calculatedHash = sha256(data.cid + secretKey + data.clientId).slice(0, 20);
      if (calculatedHash !== data.hash) {
        return { isValid: false, error: "Le hash de sécurité est invalide." };
      }

      if (data.clientId !== appointment.clientId) {
        return { isValid: false, error: "Le tag NFC ne correspond pas au client du rendez-vous." };
      }

      // Retourner également l'URL NFC si disponible
      return {
        isValid: true,
        clientId: data.clientId,
        urlsnfc: data.urlsnfc // URL NFC alternative pour iOS
      };
    } catch (error) {
      console.error("Erreur lors de la vérification des données NFC:", error);
      return { isValid: false, error: "Erreur lors de la lecture du tag NFC." };
    }
  };

  // Fonction pour le pointage NFC avec lecture réelle et état d'avancement
  const handleNFC = async () => {
    if (!isNfcAvailable) {
      setNfcError("NFC n'est pas disponible sur cet appareil.");
      return;
    }

    setIsNfcReading(true);
    setNfcError(null);

    try {
      abortControllerRef.current = new AbortController();
      const ndef = new window.NDEFReader();

      // Étape 1: Initialisation du lecteur NFC
      console.log("📱 Étape 1: Initialisation du lecteur NFC...");

      await ndef.scan({
        signal: abortControllerRef.current.signal,
        preventDefaultBehavior: true
      });

      console.log("✅ Lecteur NFC initialisé, en attente du tag...");

      // Ajouter explicitement preventDefault ici
      ndef.addEventListener("reading", (event) => {
        event.preventDefault();  // Empêche le comportement par défaut

        console.log("📱 Étape 2: Tag NFC détecté, lecture en cours...");

        const { message, serialNumber } = event;
        const decoder = new TextDecoder();
        let nfcData;

        // Parcourir tous les records pour trouver les données
        for (const record of message.records) {
          console.log(`📱 Record trouvé: ${record.recordType}`);
          if (record.recordType === "text") {
            nfcData = decoder.decode(record.data);
            console.log("✅ Données texte NFC lues:", nfcData);
            break;
          } else if (record.recordType === "url") {
            nfcData = decoder.decode(record.data);
            console.log("✅ URL NFC lue:", nfcData);
            break;
          }
        }

        if (!nfcData) {
          console.log("❌ Aucune donnée valide trouvée sur le tag NFC");
          setNfcError("Aucune donnée valide trouvée sur le tag NFC.");
          setIsNfcReading(false);
          return;
        }

        console.log("📱 Étape 3: Vérification des données NFC...");
        const { isValid, error, clientId } = verifyNfcData(nfcData);

        if (isValid) {
          console.log("✅ Données NFC validées, pointage en cours...");
          handleCheckIn('nfc', clientId);
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
        } else {
          console.log("❌ Erreur de validation NFC:", error);
          setNfcError(error);
        }

        setIsNfcReading(false);
      }, { once: true });

      // Timeout après 30 secondes
      setTimeout(() => {
        if (isNfcReading) {
          console.log("⏰ Timeout NFC: Aucun tag détecté en 30 secondes");
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          setIsNfcReading(false);
          setNfcError("Aucun tag NFC détecté. Veuillez réessayer.");
        }
      }, 30000);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("❌ Erreur lors de la lecture NFC:", error);
        setNfcError("Erreur lors de la lecture NFC. Veuillez réessayer.");
      }
      setIsNfcReading(false);
    }
  };

  // Fonction pour pointer (GPS ou NFC)
  const handleCheckIn = async (method) => {
    if (!db) return;

    try {
      const appointmentRef = doc(db, 'appointments', appointment.id);
      const checkInData = {
        checkedIn: true,
        checkInTime: Timestamp.now(),
        checkInMethod: method,
        checkInDistance: distanceInfo?.distance || null,
        checkedOut: false,
        checkOutTime: null,
        notes: null
      };

      await updateDoc(appointmentRef, {
        checkInStatus: checkInData,
        status: 'in_progress'
      });

      setCheckInStatus(checkInData);
      if (onUpdate) {
        onUpdate({ ...appointment, checkInStatus: checkInData, status: 'in_progress' });
      }

      // Rafraîchir immédiatement le rendez-vous
      // On peut aussi fermer le modal pour que l'utilisateur voie le rafraîchissement
      setTimeout(() => {
        onClose(); // Fermer le modal pour voir le rafraîchissement
      }, 500);

      alert(`Pointage ${method.toUpperCase()} effectué avec succès ! Le rendez-vous est maintenant en cours.`);

    } catch (error) {
      console.error("Erreur lors du pointage:", error);
      alert("Erreur lors du pointage. Veuillez réessayer.");
    }
  };

  // Fonction pour ouvrir le modal de clôture
  const handleCheckOut = () => {
    setShowCheckoutNotes(true);
  };

  // Fonction pour compléter le rendez-vous avec notes
  const handleCompleteWithNotes = async (notes) => {
    if (!db) return;

    try {
      const appointmentRef = doc(db, 'appointments', appointment.id);
      const checkOutData = {
        ...checkInStatus,
        checkedOut: true,
        checkOutTime: Timestamp.now(),
        notes: notes
      };

      const updateData = {
        checkInStatus: checkOutData,
        status: 'completed',
        completedTasks: completedTasks,
        notes: {
          content: notes,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        }
      };

      await updateDoc(appointmentRef, updateData);

      setCheckInStatus(checkOutData);
      if (onUpdate) {
        onUpdate({
          ...appointment,
          checkInStatus: checkOutData,
          status: 'completed',
          completedTasks: completedTasks,
          notes: {
            content: notes,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          }
        });
      }

      setShowCheckoutNotes(false);

      // Rafraîchir immédiatement le rendez-vous après la clôture
      setTimeout(() => {
        onClose(); // Fermer le modal pour voir le rafraîchissement
      }, 500);

      alert("Rendez-vous terminé avec succès !");

    } catch (error) {
      console.error("Erreur lors de la clôture du rendez-vous:", error);
      alert("Erreur lors de la clôture. Veuillez réessayer.");
    }
  };

  // Fonction pour compléter le rendez-vous sans notes
  const handleCompleteWithoutNotes = async () => {
    if (!db) return;

    // Confirmation pour éviter les erreurs
    const confirmMessage = completedTasks.length > 0
      ? "Êtes-vous sûr de vouloir terminer ce rendez-vous sans notes ? Des tâches ont été accomplies."
      : "Êtes-vous sûr de vouloir terminer ce rendez-vous sans notes ?";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const appointmentRef = doc(db, 'appointments', appointment.id);
      const checkOutData = {
        ...checkInStatus,
        checkedOut: true,
        checkOutTime: Timestamp.now(),
        notes: null
      };

      const updateData = {
        checkInStatus: checkOutData,
        status: 'completed',
        completedTasks: completedTasks
      };

      await updateDoc(appointmentRef, updateData);

      setCheckInStatus(checkOutData);

      // Ne pas fermer le modal immédiatement - laisser le parent gérer le rafraîchissement
      if (onUpdate) {
        onUpdate({
          ...appointment,
          checkInStatus: checkOutData,
          status: 'completed',
          completedTasks: completedTasks
        });
      }

      // Le modal sera fermé par le parent via onClose après le rafraîchissement
      // Ne pas utiliser setTimeout pour fermer automatiquement

      alert("Rendez-vous terminé avec succès !");

    } catch (error) {
      console.error("Erreur lors de la clôture du rendez-vous:", error);
      alert("Erreur lors de la clôture. Veuillez réessayer.");
    }
  };

  // Fonction pour détecter la plateforme et les capacités
  const detectPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isChrome = /chrome/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !isChrome;

    return {
      isIOS,
      isAndroid,
      isSafari,
      isChrome,
      isMobile: isIOS || isAndroid,
      browser: isSafari ? 'safari' : isChrome ? 'chrome' : 'other'
    };
  };

  // Fonction pour pointer avec alternative NFC (iOS) - Lecture uniquement
  const handleNfcAlternative = async () => {
    const platform = detectPlatform();

    if (!platform.isIOS) {
      setNfcError("Cette alternative NFC est uniquement pour iOS.");
      return;
    }

    try {
      setIsNfcReading(true);
      setNfcError(null);

      // Message d'information pour l'utilisateur iOS
      alert(`📱 iOS - Lecture NFC\n\nPour pointer avec NFC sur iOS:\n\n1. Utilisez un tag NFC déjà programmé via les ressources bénéficiaires\n2. Ou scannez un QR code contenant l'URL NFC\n3. L'URL NFC doit être générée dans la configuration du bénéficiaire\n\nLe pointage se fera automatiquement après lecture du tag ou scan du QR code.`);

      // Simuler une lecture en attente (en réalité, c'est l'URL qui déclenchera le pointage)
      setTimeout(() => {
        setIsNfcReading(false);
        setNfcError("Pour iOS, utilisez un tag NFC programmé ou un QR code généré dans les ressources bénéficiaires.");
      }, 2000);

    } catch (error) {
      console.error("Erreur lors de l'alternative NFC iOS:", error);
      setNfcError("Erreur lors de l'alternative NFC iOS.");
      setIsNfcReading(false);
    }
  };

  // Vérifier les capacités NFC et alternatives
  useEffect(() => {
    const platform = detectPlatform();

    // NFC natif disponible uniquement sur Android avec Chrome
    if (platform.isAndroid && 'NDEFReader' in window) {
      setIsNfcAvailable(true);
      console.log("✅ NFC natif disponible sur Android");
    } else if (platform.isIOS) {
      // iOS ne supporte pas l'API Web NFC, mais nous pouvons utiliser des alternatives
      console.log("📱 iOS - NFC natif non disponible, mais alternatives possibles");
      setIsNfcAvailable(false);

      // Vérifier si nous pouvons utiliser des URLs NFC ou d'autres alternatives
      if (appointment.clientId && appointment.clientAddress) {
        console.log("📱 iOS - Alternatives NFC disponibles via URLs");
      }
    } else {
      console.log("❌ NFC natif non disponible sur cette plateforme");
      setIsNfcAvailable(false);
    }
  }, [appointment.clientId, appointment.clientAddress]);

  // Fonction pour vérifier la distance automatiquement - identique à l'ancien modal
  const checkDistance = async () => {
    if (!appointment.clientAddress) {
      console.error("Adresse du client non disponible");
      return;
    }

    try {
      // Récupérer le rayon de pointage configuré depuis Firestore (prioritaire)
      const checkInRadius = await getCheckInRadius();
      console.log('checkDistance - Rayon configuré:', checkInRadius);

      // Essayer le géocodage, mais continuer même en cas d'erreur
      let clientCoords = null;
      try {
        clientCoords = await geocodeAddress(appointment.clientAddress);
      } catch (geocodingError) {
        console.warn("Géocodage échoué:", geocodingError);
        // En cas d'erreur de géocodage, on ne peut pas valider la distance
        // On considère que l'utilisateur n'est pas dans la zone
        setIsWithinRange(false);
        setDistanceInfo({
          valid: false,
          error: "Impossible de géocoder l'adresse du client. Vérification de distance impossible.",
          distance: null,
          formattedDistance: 'N/A',
          configuredRadius: checkInRadius,
          configuredRadiusFormatted: `${checkInRadius} mètres`
        });
        return; // Arrêter l'exécution ici
      }

      // Obtenir la position actuelle de l'utilisateur
      const currentPosition = await getCurrentPosition();

      // Valider le pointage avec le service centralisé - comme dans l'ancien modal
      const validation = validateCheckIn(
        { lat: currentPosition.lat, lng: currentPosition.lng },
        clientCoords,
        checkInRadius
      );

      // Ajouter l'information sur la distance configurée
      const enhancedValidation = {
        ...validation,
        configuredRadius: checkInRadius,
        configuredRadiusFormatted: `${checkInRadius} mètres`
      };

      setIsWithinRange(validation.valid);
      setDistanceInfo(enhancedValidation);
      setDistance(validation.formattedDistance);
      console.log('Validation résultat:', {
        valid: validation.valid,
        distance: validation.distance,
        configuredRadius: checkInRadius,
        isWithinRange: validation.valid
      });

    } catch (error) {
      console.error("Erreur lors de la vérification de la distance:", error);

      // Même en cas d'erreur, récupérer la configuration pour l'affichage
      try {
        const checkInRadius = await getCheckInRadius();
        setIsWithinRange(false);
        setDistanceInfo({
          valid: false,
          error: "Impossible de vérifier la distance. Vérifiez votre connexion GPS.",
          distance: null,
          formattedDistance: 'N/A',
          configuredRadius: checkInRadius,
          configuredRadiusFormatted: `${checkInRadius} mètres`
        });
        console.log('distanceInfo défini avec erreur:', checkInRadius);
      } catch (configError) {
        console.error("Erreur lors de la récupération de la configuration:", configError);
        setIsWithinRange(false);
        setDistanceInfo({
          valid: false,
          error: "Erreur de configuration - Impossible de récupérer la distance de pointage",
          distance: null,
          formattedDistance: 'N/A',
          configuredRadius: null,
          configuredRadiusFormatted: 'non configuré'
        });
      }
    }
  };

  // Charger les données au montage
  useEffect(() => {
    fetchTasks();
    fetchClientContract();
    fetchAppointmentHistory();
    if (appointment.clientAddress) {
      checkDistance();
    }
  }, [appointment.clientId, appointment.clientAddress]);

  return (
    <>
      <EnhancedModal onClose={onClose} size={size}>
        <ModalHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <span className="text-base font-semibold">Détails du rendez-vous</span>
            <div className="flex justify-center sm:justify-end">
              {getStatusBadge(appointment.status)}
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
            {/* Section Informations principales - Deux colonnes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Colonne gauche - Bénéficiaire */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <User className="mr-2" size={16} />
                  Bénéficiaire
                </h3>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  {/* Labels en ligne */}
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Nom :</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Téléphone :</div>
                  </div>

                  {/* Valeurs en ligne */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-gray-900 dark:text-gray-100 font-medium text-base">
                      {appointment.clientName || 'Bénéficiaire non spécifié'}
                    </div>

                    {appointment.clientPhone ? (
                      <a
                        href={`tel:${appointment.clientPhone}`}
                        className="text-blue-600 dark:text-blue-400 text-base hover:underline"
                      >
                        {appointment.clientPhone}
                      </a>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400 text-base">-</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Colonne droite - Rendez-vous */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Calendar className="mr-2" size={16} />
                  Rendez-vous
                </h3>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  {/* Labels en ligne - Date et Heure */}
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Date :</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Heure :</div>
                  </div>

                  {/* Valeurs en ligne - Date et Heure */}
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="text-gray-900 dark:text-gray-100 text-base">
                      {formatDate(appointment.date)}
                    </div>
                    <div className="text-gray-900 dark:text-gray-100 font-medium text-base">
                      {appointment.startTime} - {appointment.endTime}
                    </div>
                  </div>

                  {/* Labels en ligne - Créneau et Durée */}
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Créneau :</div>
                    {appointment.duration && (
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Durée :</div>
                    )}
                  </div>

                  {/* Valeurs en ligne - Créneau et Durée */}
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="text-gray-900 dark:text-gray-100 text-base">
                      {getTimeSlot(appointment.startTime)}
                    </div>
                    {appointment.duration && (
                      <div className="text-gray-900 dark:text-gray-100 text-base">
                        {appointment.duration} minutes
                      </div>
                    )}
                  </div>

                  {/* Intervenant */}
                  {appointment.employeeName && (
                    <>
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Intervenant :</div>
                      <div className="text-gray-900 dark:text-gray-100 text-base">
                        {appointment.employeeName}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Section Adresse et navigation - Optimisée mobile */}
            {appointment.clientAddress && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <MapPin className="mr-2" size={16} />
                  Adresse et navigation
                </h3>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 space-y-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Adresse :</span>
                    <span className="text-gray-900 dark:text-gray-100 text-sm leading-relaxed">
                      {formatAddress(appointment.clientAddress)}
                    </span>
                  </div>

                  {/* Boutons de navigation - Masqués pour les rendez-vous terminés */}
                  {appointment.status !== 'completed' && (
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => openNavigation(appointment, 'waze')}
                        className="flex items-center justify-center space-x-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-2 rounded-lg transition-colors text-sm"
                      >
                        <Navigation size={14} />
                        <span>Waze</span>
                      </button>

                      <button
                        onClick={() => openNavigation(appointment, 'google')}
                        className="flex items-center justify-center space-x-1 bg-green-500 hover:bg-green-600 text-white py-2 px-2 rounded-lg transition-colors text-sm"
                      >
                        <Map size={14} />
                        <span>Google</span>
                      </button>

                      <button
                        onClick={() => openNavigation(appointment, 'apple')}
                        className="flex items-center justify-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-2 rounded-lg transition-colors text-sm"
                      >
                        <ExternalLink size={14} />
                        <span>Apple</span>
                      </button>
                    </div>
                  )}

                  {/* Vérification de distance */}
                  <div className="text-center">
                    {distanceCalculating ? (
                      <div className="flex items-center justify-center space-x-2 text-blue-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                        <span className="text-xs">Vérification distance...</span>
                      </div>
                    ) : distance ? (
                      <div className="text-green-600 dark:text-green-400 text-xs">
                        Distance estimée : {distance}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Section Historique des interventions - Pour la liaison entre collègues */}
            {appointmentHistory.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Calendar className="mr-2" size={16} />
                  Historique des interventions
                </h3>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 space-y-3">
                  <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                    Interventions précédentes avec notes pour ce bénéficiaire
                  </p>

                  {loadingHistory ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mx-auto"></div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Chargement de l'historique...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {appointmentHistory.map((historyAppointment, index) => (
                        <div key={historyAppointment.id} className="bg-white dark:bg-gray-600 rounded-lg p-3 border border-gray-200 dark:border-gray-500">
                          {/* En-tête de l'intervention */}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex flex-col">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {formatDate(historyAppointment.date)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {historyAppointment.startTime} - {historyAppointment.endTime}
                              </span>
                            </div>
                            {historyAppointment.employeeName && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                                {historyAppointment.employeeName}
                              </span>
                            )}
                          </div>

                          {/* Tâches accomplies */}
                          {historyAppointment.completedTasks && historyAppointment.completedTasks.length > 0 && (
                            <div className="mb-2">
                              <div className="flex items-center space-x-1 mb-1">
                                <CheckCircle className="text-green-500" size={12} />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  Tâches accomplies :
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {(() => {
                                  const completedTaskIds = historyAppointment.completedTasks || [];
                                  const completedTaskObjects = tasks.filter(task => completedTaskIds.includes(task.id));

                                  return completedTaskObjects.slice(0, 3).map((task) => (
                                    <span
                                      key={task.id}
                                      className="text-xs bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-1 rounded-full"
                                    >
                                      {task.name || task.title || 'Tâche'}
                                    </span>
                                  ));
                                })()}
                                {(() => {
                                  const completedTaskIds = historyAppointment.completedTasks || [];
                                  const completedTaskObjects = tasks.filter(task => completedTaskIds.includes(task.id));
                                  if (completedTaskObjects.length > 3) {
                                    return (
                                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-2 py-1 rounded-full">
                                        +{completedTaskObjects.length - 3}
                                      </span>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Notes de l'intervention */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-1">
                                <FileText className="text-purple-500" size={12} />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  Notes :
                                </span>
                              </div>
                              {historyAppointment.employeeName && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                  par {historyAppointment.employeeName}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 rounded p-2 leading-relaxed">
                              {historyAppointment.notes?.content || historyAppointment.checkInStatus?.notes}
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Message si plus d'interventions */}
                      {appointmentHistory.length === 5 && (
                        <div className="text-center">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Affichage des 5 dernières interventions
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section Tâches à effectuer - Masquée pour les rendez-vous terminés */}
            {appointment.status !== 'completed' && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <FileText className="mr-2" size={16} />
                  Tâches à effectuer
                </h3>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 space-y-2">
                  {appointment.notes?.content && (
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-1 text-sm">Notes :</h4>
                      <p className="text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-600 rounded p-2 text-xs">
                        {appointment.notes.content}
                      </p>
                    </div>
                  )}

                  {/* Tâches du contrat client par créneau */}
                  {clientContract && clientContract.tasksPerPeriod && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">
                        Tâches pour {getTimeSlot(appointment.startTime)} :
                      </h4>

                      {(() => {
                        const timeSlot = getTechnicalTimeSlot(appointment.startTime);
                        const taskIds = clientContract.tasksPerPeriod[timeSlot] || [];
                        const slotTasks = tasks.filter(task => taskIds.includes(task.id));

                        if (slotTasks.length === 0) {
                          return (
                            <div className="text-center py-3 text-gray-500 dark:text-gray-400 text-xs">
                              <p>Aucune tâche spécifique pour ce créneau</p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-2">
                            {slotTasks.map((task) => (
                              <div
                                key={task.id}
                                className={`flex items-center space-x-2 p-2 rounded-lg transition-colors ${getPriorityClass(task.id)}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={completedTasks.includes(task.id)}
                                  onChange={() => handleTaskToggle(task.id)}
                                  disabled={!checkInStatus?.checkedIn || checkInStatus?.checkedOut}
                                  className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 ${(!checkInStatus?.checkedIn || checkInStatus?.checkedOut) ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                />
                                <label className="flex items-center justify-between w-full text-gray-900 dark:text-gray-100 text-xs">
                                  <div className="flex items-center space-x-2">
                                    <CheckCircle
                                      className={`flex-shrink-0 ${completedTasks.includes(task.id)
                                        ? 'text-green-500'
                                        : 'text-gray-400'
                                        }`}
                                      size={14}
                                    />
                                    <span className="leading-tight">{task.name || task.title || 'Tâche sans nom'}</span>
                                  </div>

                                  {/* Badge de priorité */}
                                  {taskPriorities[task.id] && (
                                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${taskPriorities[task.id].priority === 'green'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200'
                                      : taskPriorities[task.id].priority === 'orange'
                                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200'
                                      }`}>
                                      <div className={`w-2 h-2 rounded-full ${taskPriorities[task.id].priority === 'green'
                                        ? 'bg-green-500'
                                        : taskPriorities[task.id].priority === 'orange'
                                          ? 'bg-orange-500'
                                          : 'bg-red-500'
                                        }`}></div>
                                      <span className="hidden sm:inline">
                                        {getPriorityText(task.id)}
                                      </span>
                                      <span className="sm:hidden">
                                        {taskPriorities[task.id].priority === 'green' ? '🟢' :
                                          taskPriorities[task.id].priority === 'orange' ? '🟠' : '🔴'}
                                      </span>
                                    </div>
                                  )}
                                </label>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                    </div>
                  )}

                  {/* Message si pas de contrat ou pas de tâches */}
                  {(!clientContract || !clientContract.tasksPerPeriod) && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                      <p>Aucune tâche configurée pour ce client</p>
                      <p className="text-xs mt-1">Contactez l'administrateur pour configurer les tâches</p>
                    </div>
                  )}

                  {/* Résumé des tâches complétées */}
                  {completedTasks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          Tâches complétées :
                        </span>
                        <span className="text-xs text-green-600 dark:text-green-400">
                          {completedTasks.length} / {
                            clientContract && clientContract.tasksPerPeriod
                              ? (clientContract.tasksPerPeriod[getTechnicalTimeSlot(appointment.startTime)] || []).length
                              : 0
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section Pointage - Masquée pour les rendez-vous terminés */}
            {appointment.status !== 'completed' && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <CheckCircle className="mr-2" size={16} />
                  Pointage
                </h3>

                {/* Indicateur de plateforme */}
                {(() => {
                  const platform = detectPlatform();
                  if (platform.isIOS) {
                    return (
                      <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-2">
                        <div className="flex items-start">
                          <div className="bg-blue-100 dark:bg-blue-800 rounded-full p-1 mr-3">
                            <Smartphone className="text-blue-600 dark:text-blue-400" size={16} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1 text-sm">
                              iOS - Compatibilité
                            </h4>
                            <p className="text-blue-700 dark:text-blue-300 text-xs">
                              ✅ GPS fonctionne avec Safari<br />
                              🔄 NFC via URL/QR Code<br />
                              ⚠️ Chrome peut avoir des problèmes GPS
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  } else if (platform.isAndroid) {
                    return (
                      <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-3 mb-2">
                        <div className="flex items-start">
                          <div className="bg-green-100 dark:bg-green-800 rounded-full p-1 mr-3">
                            <Smartphone className="text-green-600 dark:text-green-400" size={16} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-800 dark:text-green-200 mb-1 text-sm">
                              Android - Compatibilité
                            </h4>
                            <p className="text-green-700 dark:text-green-300 text-xs">
                              ✅ GPS fonctionne<br />
                              ✅ NFC disponible<br />
                              ✅ Chrome recommandé
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 space-y-2">
                  {/* Si le rendez-vous n'est pas encore pointé */}
                  {!checkInStatus?.checkedIn && (
                    <>
                      {/* Pointage GPS - Conditionné comme dans DayView */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Pointage GPS :</h4>
                        <button
                          onClick={pointWithGPS}
                          disabled={distanceCalculating || !isWithinRange}
                          className={`flex items-center justify-center space-x-2 ${isWithinRange
                            ? 'bg-purple-500 hover:bg-purple-600'
                            : 'bg-gray-400 cursor-not-allowed'
                            } text-white py-2 px-3 rounded-lg transition-colors w-full disabled:opacity-50 text-sm`}
                        >
                          <Smartphone size={14} />
                          <span>Pointer avec GPS</span>
                        </button>

                        {/* Message si pas dans la zone */}
                        {!isWithinRange && (
                          <p className="text-xs text-red-500 text-center">
                            {distanceInfo?.error ? distanceInfo.error :
                              distanceInfo?.configuredRadius ? `Vous devez être à moins de ${distanceInfo.configuredRadius} mètres du client pour pointer avec GPS.` :
                                'Vérification de la distance en cours...'}
                          </p>
                        )}

                        {/* Vérification de distance */}
                        <div className="text-center">
                          {distanceCalculating ? (
                            <div className="flex items-center justify-center space-x-2 text-blue-500">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                              <span className="text-xs">Vérification distance...</span>
                            </div>
                          ) : distance ? (
                            <div className="text-green-600 dark:text-green-400 text-xs">
                              Distance estimée : {distance}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Pointage NFC - Conditionné comme dans DayView */}
                      {isNfcAvailable && (
                        <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Pointage NFC :</h4>
                          <button
                            onClick={handleNFC}
                            disabled={isNfcReading}
                            className={`flex items-center justify-center space-x-2 ${isNfcReading ? 'bg-yellow-500' : 'bg-indigo-500 hover:bg-indigo-600'
                              } text-white py-2 px-3 rounded-lg transition-colors w-full text-sm disabled:opacity-50`}
                          >
                            <Wifi size={14} />
                            <span>{isNfcReading ? 'Lecture NFC en cours...' : 'Pointer avec NFC'}</span>
                          </button>

                          {/* Indicateur d'avancement NFC */}
                          {isNfcReading && (
                            <div className="space-y-2">
                              {/* Barre de progression */}
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div className="bg-yellow-500 h-2 rounded-full animate-pulse"></div>
                              </div>

                              {/* Étapes d'avancement */}
                              <div className="text-center">
                                <div className="flex items-center justify-center space-x-2 text-yellow-600 dark:text-yellow-400">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500"></div>
                                  <span className="text-xs">En attente du tag NFC...</span>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Approchez le tag NFC de votre téléphone
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Messages d'erreur NFC */}
                          {nfcError && (
                            <div className="mt-2 text-red-500 flex items-center text-xs">
                              <AlertTriangle size={14} className="mr-2 flex-shrink-0" />
                              {nfcError}
                            </div>
                          )}

                        </div>
                      )}

                      {/* Alternative NFC pour iOS */}
                      {(() => {
                        const platform = detectPlatform();
                        if (platform.isIOS && !isNfcAvailable && appointment.clientId && appointment.clientAddress) {
                          return (
                            <div className="space-y-2 pt-3 border-t border-gray-200 dark:border-gray-600">
                              <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">NFC iOS (Lecture uniquement) :</h4>
                              <button
                                onClick={handleNfcAlternative}
                                disabled={isNfcReading}
                                className={`flex items-center justify-center space-x-2 ${isNfcReading ? 'bg-yellow-500' : 'bg-blue-500 hover:bg-blue-600'
                                  } text-white py-2 px-3 rounded-lg transition-colors w-full text-sm disabled:opacity-50`}
                              >
                                <Wifi size={14} />
                                <span>{isNfcReading ? 'Lecture en cours...' : 'Lire NFC iOS'}</span>
                              </button>

                              {/* Indicateur d'avancement NFC iOS */}
                              {isNfcReading && (
                                <div className="space-y-2">
                                  {/* Barre de progression */}
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div className="bg-blue-500 h-2 rounded-full animate-pulse"></div>
                                  </div>

                                  {/* Étapes d'avancement */}
                                  <div className="text-center">
                                    <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                                      <span className="text-xs">Lecture NFC en cours...</span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      Utilisez un tag NFC programmé ou un QR code
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Messages d'erreur NFC iOS */}
                              {nfcError && (
                                <div className="mt-2 text-red-500 flex items-center text-xs">
                                  <AlertTriangle size={14} className="mr-2 flex-shrink-0" />
                                  {nfcError}
                                </div>
                              )}

                              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                                Les tags NFC doivent être programmés dans les ressources bénéficiaires
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )}

                  {/* Si le rendez-vous est en cours mais pas encore clôturé */}
                  {checkInStatus?.checkedIn && !checkInStatus?.checkedOut && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Terminer le rendez-vous</h4>

                      {/* Bouton pour terminer avec notes */}
                      <button
                        onClick={handleCheckOut}
                        className="flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg transition-colors w-full text-sm"
                      >
                        <FileText size={14} />
                        <span>Terminer avec notes</span>
                      </button>

                      {/* Bouton pour terminer sans notes */}
                      <button
                        onClick={handleCompleteWithoutNotes}
                        className="flex items-center justify-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white py-2 px-3 rounded-lg transition-colors w-full text-sm"
                      >
                        <Clock size={14} />
                        <span>Terminer sans notes</span>
                      </button>

                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Choisissez d'ajouter des notes ou de terminer directement
                      </p>
                    </div>
                  )}

                  {/* Si le rendez-vous est terminé */}
                  {checkInStatus?.checkedOut && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm">Rendez-vous terminé</h4>
                      <div className="bg-green-100 dark:bg-green-900 rounded-lg p-3 text-center">
                        <CheckCircle className="text-green-600 dark:text-green-400 mx-auto mb-2" size={20} />
                        <p className="text-green-700 dark:text-green-300 text-xs">
                          Rendez-vous clôturé avec succès
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section Alertes ou informations importantes */}
            {appointment.status === 'reserve' && (
              <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                      Rendez-vous en attente de validation
                    </h4>
                    <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                      Ce rendez-vous nécessite une validation par un administrateur avant d'être confirmé.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {appointment.status === 'completed' && (
              <div className="space-y-3">
                {/* Section de confirmation de clôture */}
                <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="bg-green-100 dark:bg-green-800 rounded-full p-1 mr-3">
                      <Calendar className="text-green-600 dark:text-green-400" size={16} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-800 dark:text-green-200 mb-1">
                        Rendez-vous terminé
                      </h4>
                      <p className="text-green-700 dark:text-green-300 text-sm">
                        Ce rendez-vous a été marqué comme terminé.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section des tâches accomplies */}
                {(appointment.completedTasks && appointment.completedTasks.length > 0) && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <CheckCircle className="mr-2" size={16} />
                      Tâches accomplies
                    </h3>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                      {(() => {
                        const completedTaskIds = appointment.completedTasks || [];
                        const completedTaskObjects = tasks.filter(task => completedTaskIds.includes(task.id));

                        if (completedTaskObjects.length === 0) {
                          return (
                            <div className="text-center py-3 text-gray-500 dark:text-gray-400 text-xs">
                              <p>Aucune tâche accomplie enregistrée</p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-2">
                            {completedTaskObjects.map((task) => (
                              <div key={task.id} className="flex items-center space-x-2 bg-white dark:bg-gray-600 rounded p-2">
                                <CheckCircle className="text-green-500 flex-shrink-0" size={14} />
                                <span className="text-gray-900 dark:text-gray-100 text-xs leading-tight">
                                  {task.name || task.title || 'Tâche sans nom'}
                                </span>
                              </div>
                            ))}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Total accompli :
                              </span>
                              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                {completedTaskObjects.length} tâche{completedTaskObjects.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Section des notes de clôture */}
                {(appointment.notes?.content || (appointment.checkInStatus && appointment.checkInStatus.notes)) && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <FileText className="mr-2" size={16} />
                      Notes de clôture
                    </h3>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="bg-white dark:bg-gray-600 rounded p-3">
                        <p className="text-gray-900 dark:text-gray-100 text-sm leading-relaxed">
                          {appointment.notes?.content || appointment.checkInStatus?.notes}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section des informations de pointage - Toujours visible si disponible */}
                {appointment.checkInStatus && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      <Clock className="mr-2" size={16} />
                      Informations de pointage
                    </h3>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        {appointment.checkInStatus.checkInMethod && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Méthode :</span>
                            <span className="text-gray-900 dark:text-gray-100 capitalize">
                              {appointment.checkInStatus.checkInMethod}
                            </span>
                          </div>
                        )}
                        {appointment.checkInStatus.checkInDistance && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Distance :</span>
                            <span className="text-gray-900 dark:text-gray-100">
                              {appointment.checkInStatus.checkInDistance.toFixed(2)} mètres
                            </span>
                          </div>
                        )}
                        {appointment.checkInStatus.checkInTime && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Pointage :</span>
                            <span className="text-gray-900 dark:text-gray-100">
                              {appointment.checkInStatus.checkInTime.toDate().toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        )}
                        {appointment.checkInStatus.checkOutTime && (
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-700 dark:text-gray-300">Clôture :</span>
                            <span className="text-gray-900 dark:text-gray-100">
                              {appointment.checkInStatus.checkOutTime.toDate().toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          {canEdit && onDelete && (
            <ModalButton variant="danger" onClick={() => {
              if (window.confirm("Êtes-vous sûr de vouloir supprimer ce rendez-vous ?")) {
                onDelete(appointment.id);
              }
            }} icon={<Trash2 size={16} />}>
              Supprimer
            </ModalButton>
          )}
          {canEdit && onEdit && (
            <ModalButton variant="primary" onClick={onEdit} icon={<Edit size={16} />}>
              Modifier
            </ModalButton>
          )}
          <ModalButton variant="secondary" onClick={onClose}>
            Fermer
          </ModalButton>
        </ModalFooter>
      </EnhancedModal>

      {/* Modal de notes pour la clôture - rendu en dehors du modal parent */}
      {showCheckoutNotes && (
        <CheckoutNotesModal
          appointment={appointment}
          onClose={() => setShowCheckoutNotes(false)}
          onComplete={handleCompleteWithNotes}
          db={db}
        />
      )}
    </>
  );
};

export default EnhancedAppointmentDetailsModal;
